import React, { FC, useMemo } from "react";
import { useQuery } from "@apollo/client";
import { Lbl } from "../../../../components";
import { CustomerProjectDetails } from "../../../../redux/types/projectDetails";
import { PREVIEW_ESTIMATE } from "../../../../apolloClient/operation/querys";
import { CheckCircle, Cancel, Description } from "@mui/icons-material";
import AppLoader from "../../../../components/loader/index";

interface EstimateProps {
  handleRefetchProjectDetails: Function;
  projectData?: CustomerProjectDetails;
}

export const Estimate: FC<EstimateProps> = ({
  handleRefetchProjectDetails,
  projectData,
}) => {
  // Check if any product system requires material selection
  const anyProductRequiresMaterialSelection = projectData?.productSystem?.some((ps: any) =>
    ps.isMaterialSelectionRequired !== false
  );

  // Check if any product system requires contractor selection
  const anyProductRequiresContractorSelection = projectData?.productSystem?.some((ps: any) =>
    ps.isLaborSelectionRequired !== false
  );

  // Check if material selection is completed (only check if selection is required)
  const isMaterialSelectionComplete = useMemo(() => {
    // If no product requires material selection, consider it complete
    if (!anyProductRequiresMaterialSelection) return true;

    const materialMeasurements = projectData?.insuranceScope?.materialMeasurements || [];
    console.log("Material measurements for validation:", materialMeasurements);
    // Check if at least one material is selected
    return materialMeasurements.some((material) => material.isSelected === true);
  }, [projectData, anyProductRequiresMaterialSelection]);

  // Check if contractor selection is completed (only check if selection is required)
  const isContractorSelectionComplete = useMemo(() => {
    // If no product requires contractor selection, consider it complete
    if (!anyProductRequiresContractorSelection) return true;

    const laborMeasurements = projectData?.insuranceScope?.laborMeasurements || [];
    console.log("Labor measurements for validation:", laborMeasurements);
    // Check if any contractor/labor is selected
    return laborMeasurements.some((labor) => labor.isSelected === true);
  }, [projectData, anyProductRequiresContractorSelection]);

  // Both conditions must be met, but only if selection is required
  const canShowEstimate = isMaterialSelectionComplete && isContractorSelectionComplete;

  // Query to fetch estimate preview
  const {
    data: estimateData,
    loading: estimateLoading,
    error: estimateError,
    refetch: refetchEstimate,
  } = useQuery(PREVIEW_ESTIMATE, {
    variables: { projectId: Number(projectData?.projectId) },
    skip: !canShowEstimate || !projectData?.projectId,
    fetchPolicy: "network-only",
  });

  // Render status checks
  const renderStatusCheck = (isComplete: boolean, label: string) => (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "12px",
        padding: window.innerWidth < 768 ? "12px" : "16px",
        backgroundColor: isComplete ? "#f0fdf4" : "#fef2f2",
        borderRadius: "8px",
        border: `1px solid ${isComplete ? "#86efac" : "#fecaca"}`,
        flexWrap: "wrap",
      }}
    >
      {isComplete ? (
        <CheckCircle style={{ color: "#22c55e", fontSize: "24px" }} />
      ) : (
        <Cancel style={{ color: "#ef4444", fontSize: "24px" }} />
      )}
      <span
        style={{
          fontSize: window.innerWidth < 768 ? "14px" : "16px",
          fontWeight: 500,
          color: isComplete ? "#166534" : "#991b1b",
        }}
      >
        {label}: {isComplete ? "Complete" : "Incomplete"}
      </span>
    </div>
  );

  return (
    <div
      style={{
        padding: "16px",
        backgroundColor: "#f8fafc",
        minHeight: "100%",
        width: "100%",
      }}
    >
      {/* Header Section */}
      <div
        style={{
          marginBottom: "24px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            marginBottom: "8px",
            flexWrap: "wrap",
          }}
        >
          <Description style={{ fontSize: "28px", color: "#1976d2" }} />
          <h1
            style={{
              fontSize: "20px",
              fontWeight: 600,
              color: "#1f2937",
              margin: 0,
            }}
          >
            Estimate Preview
          </h1>
        </div>
        <p
          style={{
            fontSize: "14px",
            color: "#6b7280",
            margin: 0,
          }}
        >
          View the project estimate once all required steps are completed
        </p>
      </div>

      {/* Status Checks - Only show when not complete */}
      {!canShowEstimate && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "12px",
            marginBottom: "24px",
            width: "100%",
          }}
        >
          {renderStatusCheck(isMaterialSelectionComplete, "Material Selection")}
          {renderStatusCheck(isContractorSelectionComplete, "Contractor Selection")}
        </div>
      )}  

      {/* Main Content */}
      {!canShowEstimate ? (
        <div
          style={{
            backgroundColor: "#fff3cd",
            border: "1px solid #ffc107",
            borderRadius: "8px",
            padding: "24px",
            textAlign: "center",
          }}
        >
          <Lbl
            title="Requirements Not Met"
            size={20}
            bold={600}
            color="#856404"
            backgroundColor="rgb(255, 243, 205)"
          />
          <p
            style={{
              fontSize: "16px",
              color: "#856404",
              marginTop: "12px",
              marginBottom: 0,
            }}
            className="text-wrap"
          >
            Please complete both Material Selection and Contractor Selection to view the estimate.
          </p>
        </div>
      ) : estimateLoading ? (
        <AppLoader isLoading={true} />
      ) : estimateError ? (
        <div
          style={{
            backgroundColor: "#fee",
            border: "1px solid #fcc",
            borderRadius: "8px",
            padding: "24px",
            textAlign: "center",
          }}
        >
          <Lbl
            title="Error Loading Estimate"
            size={20}
            bold={600}
            color="#dc3545"
          />
          <p
            style={{
              fontSize: "16px",
              color: "#dc3545",
              marginTop: "12px",
              marginBottom: 0,
            }}
          >
            {estimateError.message || "Failed to load estimate preview"}
          </p>
        </div>
      ) : estimateData?.previewEstimate ? (
        <div
          style={{
            backgroundColor: "#ffffff",
            borderRadius: "12px",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
            padding: "24px",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: window.innerWidth < 768 ? "column" : "row",
              justifyContent: "space-between",
              alignItems: window.innerWidth < 768 ? "flex-start" : "center",
              marginBottom: "24px",
              gap: window.innerWidth < 768 ? "12px" : "0",
            }}
          >
            <Lbl
              title="Estimate PDF"
              size={22}
              bold={600}
              color="#1f2937"
            />
            {estimateData.previewEstimate.url && (
              <a
                href={estimateData.previewEstimate.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  backgroundColor: "#1976d2",
                  color: "#ffffff",
                  padding: "10px 20px",
                  borderRadius: "6px",
                  textDecoration: "none",
                  fontSize: "14px",
                  fontWeight: 500,
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <Description style={{ fontSize: "20px" }} />
                Open PDF
              </a>
            )}
          </div>
          
          {/* PDF Preview */}
          {estimateData.previewEstimate.url && (
            <div
              style={{
                width: "100%",
                height: "800px",
                border: "1px solid #e5e7eb",
                borderRadius: "8px",
                overflow: "hidden",
              }}
            >
              <iframe
                src={`${estimateData.previewEstimate.url}#navpanes=0`}
                style={{
                  width: "100%",
                  height: "100%",
                  border: "none",
                }}
                title="Estimate PDF Preview"
              />
            </div>
          )}
          
          {/* Estimate Info */}
          <div
            style={{
              marginTop: "16px",
              padding: "12px",
              backgroundColor: estimateData.previewEstimate.signed ? "#f0fdf4" : "#fef3c7",
              borderRadius: "6px",
              fontSize: "14px",
              color: estimateData.previewEstimate.signed ? "#166534" : "#92400e",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <strong>Status:</strong>{" "}
            {estimateData.previewEstimate.signed ? "Signed" : "Not Signed"}
          </div>
        </div>
      ) : (
        <div
          style={{
            backgroundColor: "#f3f4f6",
            border: "1px solid #d1d5db",
            borderRadius: "8px",
            padding: "24px",
            textAlign: "center",
          }}
        >
          <Lbl
            title="No Estimate Available"
            size={20}
            bold={600}
            color="#6b7280"
          />
          <p
            style={{
              fontSize: "16px",
              color: "#6b7280",
              marginTop: "12px",
              marginBottom: 0,
            }}
          >
            No estimate has been generated for this project yet.
          </p>
        </div>
      )}
    </div>
  );
};
